import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button, Input, Logo } from '../components/common';
import { useRegister } from '../hooks/useAuth';
import { checkEmail } from '../services/api/auth.api';

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');

  const [agreeAge, setAgreeAge] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [modalType, setModalType] = useState<'terms' | 'privacy'>('terms');
  const emailCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const registerMutation = useRegister();

  // 이메일 중복체크 (debounce 500ms)
  useEffect(() => {
    if (emailCheckTimer.current) {
      clearTimeout(emailCheckTimer.current);
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setEmailStatus('idle');
      return;
    }

    setEmailStatus('checking');
    emailCheckTimer.current = setTimeout(async () => {
      try {
        const available = await checkEmail(email);
        setEmailStatus(available ? 'available' : 'taken');
      } catch {
        setEmailStatus('idle');
      }
    }, 500);

    return () => {
      if (emailCheckTimer.current) {
        clearTimeout(emailCheckTimer.current);
      }
    };
  }, [email]);

  const validatePassword = (pwd: string) => {
    if (pwd.length < 6) {
      return '비밀번호는 최소 6자 이상이어야 합니다.';
    }
    return '';
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordError(validatePassword(newPassword));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 이메일 중복 체크
    if (emailStatus === 'taken') {
      return;
    }

    // 비밀번호 유효성 검사
    const error = validatePassword(password);
    if (error) {
      setPasswordError(error);
      return;
    }

    // 비밀번호 일치 확인
    if (password !== confirmPassword) {
      setPasswordError('비밀번호가 일치하지 않습니다.');
      return;
    }

    // 약관 동의 확인
    if (!agreeAge || !agreeTerms || !agreePrivacy) {
      setTermsError('모든 필수 약관에 동의하셔야 합니다.');
      return;
    }
    setTermsError('');

    registerMutation.mutate({ email, password, username });
  };

  return (
    <div className="hell-scope relative min-h-screen flex items-center justify-center bg-[var(--bg)] p-4 overflow-hidden font-sans">
      {/* 1. 사이버펑크 격자(Grid) 배경 */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(168, 85, 247, 0.1) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(168, 85, 247, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* 2. 스캔라인 및 노이즈 미세 오버레이 */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_center,transparent_30%,var(--bg)_100%)] z-1" />

      {/* 3. 대형 네온 글로우 오브 */}
      <div className="absolute top-1/4 right-1/4 translate-x-1/2 -translate-y-1/2 w-[35rem] h-[35rem] bg-[var(--accent)]/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/4 -translate-x-1/2 translate-y-1/2 w-[35rem] h-[35rem] bg-[var(--accent)]/5 rounded-full blur-[120px] pointer-events-none" />

      <motion.div
        className="relative w-full max-w-md z-10 my-8"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* 하이테크 느낌의 미세한 사선 데코 라인 */}
        <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-[var(--accent)] pointer-events-none opacity-80" />
        <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-[var(--accent)] pointer-events-none opacity-80" />

        {/* 메인 회원가입 컨테이너 */}
        <div className="backdrop-blur-xl bg-[var(--card)]/60 rounded-3xl p-8 border border-[var(--border)] shadow-[0_0_50px_rgba(0,0,0,0.8),inset_0_1px_1px_rgba(255,255,255,0.1)]">
          {/* 로고 및 서비스 명 */}
          <div className="flex flex-col items-center mb-6">
            <div className="drop-shadow-[0_0_15px_rgba(168,85,247,0.4)]">
              <Logo size="lg" />
            </div>
            <span className="text-[10px] tracking-[0.3em] font-black uppercase text-[var(--accent)] mt-3 drop-shadow-[0_0_5px_var(--accent)]">
              HERO CREATION PROTOCOL
            </span>
          </div>

          {/* 제목 */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-extrabold tracking-tight text-[var(--text)] mb-2">
              회원가입
            </h2>
            <p className="text-xs text-[var(--text-secondary)] font-bold uppercase tracking-wider">
              피트니스 성장을 위한 여정을 시작하세요
            </p>
          </div>

          {/* 에러 메시지 */}
          {registerMutation.isError && (() => {
            const error = registerMutation.error as any;
            let errorMessage = '회원가입에 실패했습니다. 다시 시도해주세요.';
            const details: Array<{ field: string; message: string }> = [];

            if (error?.response?.data?.error?.message) {
              errorMessage = error.response.data.error.message;
              if (Array.isArray(error.response.data.error.details)) {
                details.push(...error.response.data.error.details);
              }
            } else if (error?.response?.data?.message) {
              errorMessage = error.response.data.message;
            } else if (error?.response?.status === 409) {
              errorMessage = '이미 사용 중인 이메일 또는 사용자명입니다.';
            } else if (error?.response?.status === 400) {
              errorMessage = '입력한 정보를 확인해주세요.';
            } else if (error?.response?.status === 500) {
              errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
            } else if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
              errorMessage = '요청 시간이 초과되었습니다. 네트워크 연결을 확인해주세요.';
            } else if (error?.code === 'ERR_NETWORK' || !error?.response) {
              errorMessage = '네트워크 연결에 실패했습니다. 인터넷 연결을 확인해주세요.';
            }

            return (
              <motion.div
                className="mb-6 p-4 bg-[var(--danger)]/40 border border-[var(--danger)]/50 rounded-xl"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <p className="text-[var(--danger)] text-xs font-semibold leading-relaxed">{errorMessage}</p>
                {details.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {details.map((d, i) => (
                      <li key={i} className="text-[var(--danger)] text-[11px] font-medium">
                        • {d.message}
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            );
          })()}

          {/* 회원가입 폼 */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="email"
                label="이메일"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                name="email"
                className="bg-[var(--bg)]/30 border-[var(--border)] text-[var(--text)] placeholder-[var(--text-muted)] focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 transition-all duration-300 rounded-xl px-4 py-3"
                leftIcon={
                  <svg
                    className="w-5 h-5 text-[var(--text-muted)]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                    />
                  </svg>
                }
              />
              {emailStatus === 'checking' && (
                <p className="text-[var(--text-muted)] text-[10px] mt-1 font-bold animate-pulse">이메일 중복 확인 중...</p>
              )}
              {emailStatus === 'available' && (
                <motion.p
                  className="text-emerald-400 text-[10px] mt-1 font-bold flex items-center gap-1"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  사용 가능한 이메일입니다.
                </motion.p>
              )}
              {emailStatus === 'taken' && (
                <motion.p
                  className="text-rose-400 text-[10px] mt-1 font-bold"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  이미 사용 중인 이메일 주소입니다.
                </motion.p>
              )}
            </div>

            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                label="비밀번호"
                placeholder="••••••••"
                value={password}
                onChange={handlePasswordChange}
                required
                autoComplete="one-time-code"
                name="password"
                className="bg-[var(--bg)]/30 border-[var(--border)] text-[var(--text)] placeholder-[var(--text-muted)] focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 transition-all duration-300 rounded-xl px-4 py-3"
                leftIcon={
                  <svg
                    className="w-5 h-5 text-[var(--text-muted)]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                }
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-[44px] text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>

            {password && passwordError && (
              <motion.p
                className="text-rose-400 text-[10px] mt-1 font-bold"
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {passwordError}
              </motion.p>
            )}

            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                label="비밀번호 확인"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="one-time-code"
                name="confirm-password"
                className="bg-[var(--bg)]/30 border-[var(--border)] text-[var(--text)] placeholder-[var(--text-muted)] focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 transition-all duration-300 rounded-xl px-4 py-3"
                leftIcon={
                  <svg
                    className="w-5 h-5 text-[var(--text-muted)]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                }
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-[44px] text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
              >
                {showConfirmPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>

            {confirmPassword && password !== confirmPassword && (
              <motion.p
                className="text-rose-400 text-[10px] mt-1 font-bold"
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
              >
                비밀번호가 일치하지 않습니다.
              </motion.p>
            )}

            {confirmPassword && password === confirmPassword && !passwordError && (
              <motion.p
                className="text-emerald-400 text-[10px] mt-1 font-bold flex items-center gap-1"
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                비밀번호가 일치합니다.
              </motion.p>
            )}

            <Input
              type="text"
              label="사용자 이름"
              placeholder="영웅의 이름을 입력하세요"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
              name="username"
              className="bg-[var(--bg)]/30 border-[var(--border)] text-[var(--text)] placeholder-[var(--text-muted)] focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 transition-all duration-300 rounded-xl px-4 py-3"
              leftIcon={
                <svg
                  className="w-5 h-5 text-[var(--text-muted)]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              }
            />

            {/* 약관 동의 체크박스 */}
            <div className="space-y-3 mt-5 border-t border-[var(--border)]/50 pt-4 text-left">
              <div className="flex items-center">
                <input
                  id="agree-all"
                  type="checkbox"
                  checked={agreeAge && agreeTerms && agreePrivacy}
                  onChange={(e) => {
                     const checked = e.target.checked;
                     setAgreeAge(checked);
                     setAgreeTerms(checked);
                     setAgreePrivacy(checked);
                  }}
                  className="w-4 h-4 text-[var(--accent)] bg-[var(--bg)]/40 border-[var(--border)] rounded focus:ring-[var(--accent)] cursor-pointer"
                />
                <label htmlFor="agree-all" className="ml-2 text-xs font-bold text-[var(--text)] cursor-pointer select-none tracking-wider">
                  모든 이용약관 및 정책에 동의합니다.
                </label>
              </div>

              <div className="space-y-2 pl-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="agree-age"
                      type="checkbox"
                      required
                      checked={agreeAge}
                      onChange={(e) => setAgreeAge(e.target.checked)}
                      className="w-4 h-4 text-[var(--accent)] bg-[var(--bg)]/40 border-[var(--border)] rounded focus:ring-[var(--accent)] cursor-pointer"
                    />
                    <label htmlFor="agree-age" className="ml-2 text-[11px] font-semibold text-[var(--text-muted)] cursor-pointer select-none">
                      <span className="text-[var(--accent)] font-extrabold mr-1">[필수]</span>만 14세 이상 등록 동의
                    </label>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="agree-terms"
                      type="checkbox"
                      required
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      className="w-4 h-4 text-[var(--accent)] bg-[var(--bg)]/40 border-[var(--border)] rounded focus:ring-[var(--accent)] cursor-pointer"
                    />
                    <label htmlFor="agree-terms" className="ml-2 text-[11px] font-semibold text-[var(--text-muted)] cursor-pointer select-none">
                      <span className="text-[var(--accent)] font-extrabold mr-1">[필수]</span>서비스 이용약관 동의
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setModalType('terms');
                      setShowTermsModal(true);
                    }}
                    className="text-[10px] text-[var(--accent)] hover:opacity-80 underline font-bold focus:outline-none"
                  >
                    보기
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="agree-privacy"
                      type="checkbox"
                      required
                      checked={agreePrivacy}
                      onChange={(e) => setAgreePrivacy(e.target.checked)}
                      className="w-4 h-4 text-[var(--accent)] bg-[var(--bg)]/40 border-[var(--border)] rounded focus:ring-[var(--accent)] cursor-pointer"
                    />
                    <label htmlFor="agree-privacy" className="ml-2 text-[11px] font-semibold text-[var(--text-muted)] cursor-pointer select-none">
                      <span className="text-[var(--accent)] font-extrabold mr-1">[필수]</span>개인정보 수집/이용 동의
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setModalType('privacy');
                      setShowTermsModal(true);
                    }}
                    className="text-[10px] text-[var(--accent)] hover:opacity-80 underline font-bold focus:outline-none"
                  >
                    보기
                  </button>
                </div>
              </div>
            </div>

            {termsError && (
              <p className="text-rose-400 text-[10px] mt-1 font-bold">{termsError}</p>
            )}

            <div className="pt-2">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                isLoading={registerMutation.isPending}
                className="text-[var(--text)] font-extrabold tracking-widest uppercase transition-all duration-300 active:scale-98 rounded-xl py-3.5 border-none"
              >
                가입 완료 및 캐릭터 생성
              </Button>
            </div>
          </form>

          {/* 구분선 */}
          <div className="relative my-7">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--border)]"></div>
            </div>
            <div className="relative flex justify-center text-[10px] tracking-wider uppercase font-black">
              <span className="px-4 bg-[var(--card)] text-[var(--text-muted)]">OR</span>
            </div>
          </div>

          {/* 로그인 링크 */}
          <div className="text-center">
            <p className="text-xs text-[var(--text-secondary)] font-bold">
              이미 시스템에 코어가 등록되어 있으신가요?{' '}
              <Link
                to="/login"
                className="text-[var(--accent)] hover:opacity-85 font-extrabold transition-colors ml-1 underline"
              >
                로그인 화면으로
              </Link>
            </p>
          </div>
        </div>

        {/* 하단 텍스트 */}
        <motion.p
          className="text-center text-[var(--text-muted)] text-[10px] tracking-[0.2em] uppercase font-black mt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          FITNESS RPG ● BEGIN YOUR TRANSFORMATION
        </motion.p>
      </motion.div>

      {/* 약관 상세 보기 모달 */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg bg-[var(--card)] border border-[var(--border)] rounded-3xl overflow-hidden shadow-2xl relative"
          >
            {/* 사선 데코 라인 */}
            <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-[var(--accent)] pointer-events-none opacity-80" />
            <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-[var(--accent)] pointer-events-none opacity-80" />

            {/* 모달 헤더 */}
            <div className="flex justify-between items-center p-6 border-b border-[var(--border)]/30">
              <h3 className="text-sm font-black tracking-widest text-[var(--accent)] uppercase">
                {modalType === 'terms' ? '서비스 이용약관' : '개인정보처리방침'}
              </h3>
              <button
                type="button"
                onClick={() => setShowTermsModal(false)}
                className="text-[var(--text-muted)] hover:text-[var(--text)] transition-colors focus:outline-none"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* 모달 바디 */}
            <div className="p-6 max-h-[45vh] overflow-y-auto text-[var(--text-secondary)] text-xs whitespace-pre-line leading-relaxed text-left font-mono scrollbar-thin scrollbar-thumb-[var(--border)]">
              {modalType === 'terms' ? TERMS_CONTENT : PRIVACY_CONTENT}
            </div>
            {/* 모달 푸터 */}
            <div className="p-4 bg-black/40 border-t border-[var(--border)]/30 flex justify-end">
              <Button
                type="button"
                variant="primary"
                className="text-[var(--bg)] font-extrabold text-xs px-5 py-2.5 rounded-xl transition-all border-none"
                onClick={() => {
                  if (modalType === 'terms') {
                    setAgreeTerms(true);
                  } else {
                    setAgreePrivacy(true);
                  }
                  setShowTermsModal(false);
                }}
              >
                동의하고 닫기
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

const TERMS_CONTENT = `[헬게이터 서비스 이용약관]

제1조 (목적)
본 약관은 "헬게이터"(이하 "서비스")가 제공하는 게임형 피트니스 트래킹 웹/앱 서비스의 이용과 관련하여, 서비스와 회원 간의 권리, 의무, 책임사항 및 기타 필요한 사항을 규정함을 목적으로 합니다.

제2조 (서비스의 제공 및 변경)
1. 서비스는 회원에게 다음과 같은 서비스를 제공합니다.
   * RPG 요소를 결합한 운동 기록 및 관리 기능
   * 신체 측정 기록 및 비포/애프터 사진 관리 기능
   * 인공지능 기반 1RM 측정 분석 및 레벨 테스트 기능
   * 캐릭터 성장, 스킬 트리, 맵 탐색 등 게임화 서비스
   * 유료 아이템 구매 및 결제 서비스
2. 서비스는 운영상, 기술상의 필요에 따라 제공하고 있는 서비스의 전부 또는 일부를 변경하거나 중단할 수 있습니다.

제3조 (의무 및 면책 조항)
1. 회원은 본인의 건강 상태에 맞추어 무리하지 않는 범위 내에서 운동을 수행해야 합니다. 서비스 내에서 제공되는 운동 가이드, 1RM 추천 무게 등은 참고용일 뿐 의학적 또는 전문적 체육 지도 소견을 대체하지 않습니다.
2. 서비스는 회원이 서비스를 이용하는 과정에서 발생한 신체적 부상, 부주의로 인한 안전사고, 물리적 손해 등에 대해 관련 법령에 특별한 규정이 없는 한 책임을 지지 않습니다.

제4조 (유료 결제 및 청약철회)
1. 회원은 서비스 내에서 제공하는 유료 서비스(멤버십, 아이템 등)를 구매할 수 있습니다.
2. 유료 결제는 전자결제 대행업체(PG)의 규정에 따르며, 디지털 콘텐츠의 특성상 구매 후 즉시 사용되거나 혜택이 적용된 상품은 청약철회(환불)가 제한될 수 있습니다.`;

const PRIVACY_CONTENT = `[헬게이터 개인정보처리방침]

개인정보 보호법에 따라 이용자의 개인정보를 보호하고 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 하기 위하여 다음과 같이 개인정보처리방침을 수립·공개합니다.

제1조 (개인정보의 수집 및 이용 목적)
서비스는 다음의 목적을 위해 최소한의 개인정보를 수집하며, 목적 외의 용도로는 사용하지 않습니다.
1. 회원 가입 및 관리: 회원 가입 의사 확인, 회원제 서비스 제공에 따른 본인 식별·인증, 회원자격 유지·관리, 부정이용 방지
2. 서비스 제공 및 RPG 게임 요소 운영: 운동 기록 분석, 1RM 측정, 캐릭터 레벨/스킬 및 리더보드 순위 반영
3. 고객 문의 대응: 서비스 이용 관련 버그 리포트, 건의사항 확인 및 결과 통보
4. 결제 및 서비스 구매 처리: 유료 결제 시 결제 승인, 환불 처리 및 세금계산서 발급

제2조 (수집하는 개인정보 항목)
회원가입 및 서비스 이용 과정에서 다음과 같은 개인정보가 수집됩니다.
* 필수 수집 항목: 이메일 주소(아이디), 비밀번호, 사용자 이름(닉네임)
* 서비스 이용 중 수집 항목: 운동 기록 데이터(세트 수, 횟수, 무게), 신체 측정 기록(체중, 골격근량, 체지방률, 신체 둘레 등), 비포/애프터 사진 및 운동 시연 동영상 파일
* 유료 결제 시 수집 항목: 결제 수단 정보(PG사에서 직접 처리하며, 서비스 서버에는 신용카드 번호 등을 직접 저장하지 않음)
* 자동 생성 정보: IP 주소, 쿠키, 서비스 이용 기록, 기기 정보, 브라우저 정보

제3조 (개인정보의 보유 및 이용 기간)
1. 이용자의 개인정보는 회원 탈퇴 시 혹은 수집·이용 목적이 달성되면 지체 없이 파기합니다.
2. 단, 전자상거래법 등 관계 법령의 규정에 의하여 보존할 필요가 있는 경우 관련 법령에서 정한 일정 기간 동안 회원 정보를 보관합니다.
   * 소비자의 불만 또는 분쟁처리에 관한 기록: 3년 (전자상거래법)
   * 계약 또는 청약철회 등에 관한 기록: 5년 (전자상거래법)
   * 대금결제 및 재화 등의 공급에 관한 기록: 5년 (전자상거래법)`;
